import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from './Avatar';
import { authorHandle, profilePath } from '../lib/display';
import type { Submission } from '../lib/types';
import './SubmissionModal.css';

interface Props {
  submission: Submission;
  /** Whether the viewer can like (i.e. is signed in). */
  canLike: boolean;
  /** Toggle a like on this submission. */
  onLike: (submission: Submission) => void;
  /** Admin-only: remove this submission. Renders a Remove button when set. */
  onRemove?: (submission: Submission) => void;
  /** Close (× / Esc / backdrop). */
  onClose: () => void;
}

/**
 * Detail modal for a single Redesign Challenge submission: large image, the
 * member's description + author identity, and a like button that shares the
 * same optimistic handler as the grid tile. Accessible dialog with Esc /
 * backdrop close, focus management, and a Tab trap.
 */
export function SubmissionModal({ submission, canLike, onLike, onRemove, onClose }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const liked = !!submission.viewer_liked;
  const authorName = authorHandle(submission.author);
  const authorTo = profilePath(submission.author);

  // Body scroll lock + initial focus.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Esc to close, Tab trapped within the dialog.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'Tab') {
        const root = overlayRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="submodal"
      role="dialog"
      aria-modal="true"
      aria-label={`Design by ${authorName}`}
      onClick={onBackdropClick}
    >
      <div className="submodal__panel">
        <button
          ref={closeRef}
          type="button"
          className="submodal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="submodal__media">
          <img className="submodal__image" src={submission.image_url} alt={`Design by ${authorName}`} />
        </div>

        <div className="submodal__aside">
          {authorTo ? (
            <Link to={authorTo} className="submodal__author submodal__author--link">
              <Avatar name={authorName} src={submission.author?.avatar_url} size="md" />
              <span className="submodal__author-name">@{authorName}</span>
            </Link>
          ) : (
            <div className="submodal__author">
              <Avatar name={authorName} src={submission.author?.avatar_url} size="md" />
              <span className="submodal__author-name">@{authorName}</span>
            </div>
          )}

          {submission.body && <p className="submodal__text">{submission.body}</p>}

          <button
            type="button"
            className={`sublike sublike--lg ${liked ? 'sublike--on' : ''}`}
            onClick={() => onLike(submission)}
            disabled={!canLike}
            aria-pressed={liked}
            aria-label={liked ? 'Unlike this design' : 'Like this design'}
          >
            <span aria-hidden="true">{liked ? '♥' : '♡'}</span>
            <span>{submission.like_count}</span>
          </button>

          {onRemove && (
            <button
              type="button"
              className="submodal__remove"
              onClick={() => onRemove(submission)}
            >
              Remove design
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
